import classNames from 'classnames';

import type { CSSProperties } from 'react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import CrossIcon from '@/assets/images/icons/clear.svg';
import DoneIcon from '@/assets/images/icons/done.svg';
import ErrorIcon from '@/assets/images/icons/error.svg';
import InfoIcon from '@/assets/images/icons/info.svg';
import WarningIcon from '@/assets/images/icons/warning.svg';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { removeSnack } from '@/store/slices/snackSlice';

import styles from './styles.module.scss';

export type SnackbarVariants = 'success' | 'warning' | 'error' | 'info';

export interface SnackbarProps {
  isOpen?: boolean,
  id: string,
  children?: React.ReactNode,
  variant?: SnackbarVariants,
  autoCloseTime?: number,
  style?: CSSProperties,
  lngKey?: string,
}

const Snackbar = ({
  isOpen = true,
  id,
  children,
  variant = 'success',
  autoCloseTime = 3000,
  style,
  lngKey,
}: SnackbarProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [beforeClose, setBeforeClose] = useState(false);
  const [isOpened, setIsOpened] = useState(isOpen);
  const SnackIcon = variant === 'warning'
    ? <WarningIcon />
    : variant === 'error'
      ? <ErrorIcon />
      : variant === 'info'
        ? <InfoIcon />
        : <DoneIcon />;
  const cn = classNames(styles.snackbar, styles[variant], beforeClose && styles.beforeClose);

  const close = () => {
    dispatch(removeSnack(id));
    setIsOpened(false);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const timerId = setTimeout(() => close(), autoCloseTime);
    const aniId = setTimeout(() => setBeforeClose(true), autoCloseTime - 300);
    return () => {
      setBeforeClose(false);
      clearTimeout(timerId);
      clearTimeout(aniId);
    };
  }, [isOpen, isOpened, children]);

  if (isOpened) {
    return createPortal(
      <div className={cn} style={style}>
        {SnackIcon}
        {lngKey && t(lngKey)}
        {children}
        <CrossIcon onClick={close} />
      </div>,
      document.getElementById('root')!,
    );
  }
  return null;
};

export default Snackbar;
